import { ACTIONS, initialState, reducer } from "./collectReducer";
import { useReducer, useContext } from "react";
import AuthContext from "../../components/context/AuthContext";
import axios from "axios";
import useImageProcessing from "./imageProcessing/useImageProcessing";
import useLocation from "../../hooks/useLocation";

const useCollect = ({ selectedPhotoPoint }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { location } = useLocation();
  const { processUploadedImages } = useImageProcessing();
  const authCtx = useContext(AuthContext);

  // Update component's inital data to the selected photo point.
  const initializeComponent = () => {
    dispatch({
      type: ACTIONS.RESET_STATE,
    });
    dispatch({
      type: ACTIONS.INITIALIZE_FORM,
      value: selectedPhotoPoint,
    });
  };

  // Updates state from the useEffect's address fetch response.
  const addressResponseHandler = (response) => {
    response && response.data
      ? dispatch({
          type: ACTIONS.SET_ADDRESS,
          value: response.data,
        })
      : dispatch({
          type: ACTIONS.SET_ADDRESS,
          value: "Unable to fetch nearest built address.",
        });
  };

  // Updates state from the useEffect's address fetch response if it errored.
  const addressResponseErrorHandler = (error) => {
    dispatch({
      type: ACTIONS.SET_ADDRESS,
      value: `Failed to fetch nearest built address: ${error?.messsage}`,
    });
  };

  // Updates state from the submission response handler.
  const submissionResponseHandler = (response) => {
    response && response.data
      ? dispatch({
          type: ACTIONS.SET_SUBMISSION_STATUS,
          value: "Data submitted successfully!",
        })
      : dispatch({
          type: ACTIONS.SET_STATUS,
          value: "Submission error: Invalid response returned.",
        });
  };

  // User input condition toggle handler.
  const conditionToggle = () => {
    dispatch({ type: ACTIONS.CONDITION_TOGGLE });
  };

  // User input comments change handler.
  const commentsChangeHandler = (event) => {
    dispatch({ type: ACTIONS.COMMENTS_INPUT, value: event.target.value });
  };

  // User input feature select handler.
  const featureSelectHandler = (selectedOption) => {
    dispatch({ type: ACTIONS.FEATURE_SELECTED, value: selectedOption });
  };

  // Method to clear the state's status.
  const clearStatus = () => {
    dispatch({
      type: ACTIONS.SET_STATUS,
      value: "",
    });
  };

  // Method to set the state's photo preview status.
  const setPreview = (photo) => {
    dispatch({
      type: ACTIONS.SET_PREVIEW,
      value: photo,
    });
  };

  // Attaches images to the submission form object before returning it.
  const attachImages = async ({ state, formData }) => {
    for await (let image of state.imageObjects) {
      if (state.imageError) {
        break;
      }
      await axios
        .get(image.url, { responseType: "blob" })
        .then((response) => {
          formData.append(image.key, response.data);
        })
        .catch((error) => {
          dispatch({
            type: ACTIONS.FORM_IMAGE_ERROR,
            value: `${error.title}: ${error.message}`,
          });
        });
    }
    return formData;
  };

  // Creates the submission form object.
  const createSubmissionObject = async () => {
    let formData = new FormData();
    formData.append("createdUser", authCtx?.user?.username);
    formData.append("condition", state.condition ? "Good" : "Bad");
    formData.append("feature", state.selectedFeature.value);
    formData.append("comments", state.comments);
    formData.append("latitude", selectedPhotoPoint?.LOCATION?.latitude);
    formData.append("longitude", selectedPhotoPoint?.LOCATION?.longitude);
    formData.append("userLatitude", selectedPhotoPoint?.USER_LATITUDE);
    formData.append("userLongitude", selectedPhotoPoint?.USER_LONGITUDE);
    formData.append(
      "structureName",
      selectedPhotoPoint.STRUCTURE_NAME ? selectedPhotoPoint.STRUCTURE_NAME : ""
    );

    if (selectedPhotoPoint?.OBJECTID) {
      formData.append("id", selectedPhotoPoint.OBJECTID);
    }
    if (selectedPhotoPoint?.THREEGISID) {
      formData.append("threegisid", selectedPhotoPoint.THREEGISID);
    }
    if (selectedPhotoPoint.ACCOUNTCODE) {
      formData.append("accountcode", selectedPhotoPoint.ACCOUNTCODE);
    }
    // Add fields if the condition is being changed from good to bad to trigger the alert emails.
    if (
      selectedPhotoPoint?.CONDITION?.toLowerCase() === "good" &&
      state.condition === false
    ) {
      formData.append("lastEditedUser", selectedPhotoPoint?.LAST_EDITED_USER);
      formData.append("sendAlertEmail", true);
    }
    formData = await attachImages({ state: state, formData: formData });
    return formData;
  };

  // Handles processing of images.
  const processImagesHandler = async (event) => {
    try {
      var files = event?.target?.files;
      if (!files) return;
      dispatch({ type: ACTIONS.PROCESSING_IMAGE, value: true });
      if (state.imageObjects.length + files.length <= state.maxPhotoCount) {
        let images = await processUploadedImages({
          ACTIONS: ACTIONS,
          dispatch: dispatch,
          files: files,
          selectedPhotoPoint: selectedPhotoPoint,
          state: state,
        });
        dispatch({
          type: ACTIONS.UPDATE_IMAGE_OBJECTS,
          value: images,
        });
      } else {
        dispatch({
          type: ACTIONS.SET_STATUS,
          value: `May not upload more than ${state.maxPhotoCount} total photos.`,
        });
      }
      dispatch({ type: ACTIONS.PROCESSING_IMAGE, value: false });
    } catch (e) {
      dispatch({
        type: ACTIONS.SET_STATUS,
        value: `Error processing image: ${e.message}`,
      });
    }
  };

  // Handles removing photos from the list.
  const removePhotoHandler = (previewPhoto) => {
    if (!state.imageObjects || state.imageObjects?.length === 0) return;
    let index = state.imageObjects.findIndex(
      (img) =>
        previewPhoto.lastModified === img.lastModified &&
        previewPhoto.size === img.size &&
        previewPhoto.type === img.type
    );
    if (index >= 0) {
      dispatch({
        type: ACTIONS.REMOVE_PHOTO,
        value: index,
      });
    }
  };

  // Handles user submission button click.
  const submitCreateEditHandler = async (event) => {
    event.preventDefault();
    let formData = await createSubmissionObject();

    // Guards.
    if (state.formImageError) return;
    if (selectedPhotoPoint?.HAS_PHOTO_POINT == null) {
      dispatch({
        type: ACTIONS.SET_STATUS,
        value: "Transaction blocked, unable to determine target URL API.",
      });
      return;
    }

    // Submit the request.
    dispatch({
      type: ACTIONS.LOADING_TOGGLE,
    });
    // Determine if creating or editing.
    var url = selectedPhotoPoint.HAS_PHOTO_POINT
      ? `${process.env.REACT_APP_API_URL}/photoPoint/edit`
      : `${process.env.REACT_APP_API_URL}/photoPoint/create`;
    let authObj = { headers: { Authorization: `token ${authCtx.token}` } };
    axios
      .post(url, formData, authObj)
      .then((response) => {
        submissionResponseHandler(response);
      })
      .catch((error) => {
        submissionResponseHandler(error);
      });
  };

  return {
    addressResponseErrorHandler,
    addressResponseHandler,
    authCtx,
    clearStatus,
    commentsChangeHandler,
    conditionToggle,
    featureSelectHandler,
    initializeComponent,
    location,
    processImagesHandler,
    removePhotoHandler,
    setPreview,
    state,
    submitCreateEditHandler,
  };
};

export default useCollect;
